import i18next from 'i18next';

/**
 * Code groups with a display label in the catalog (`codes.<group>.<CODE>` in en.json). The API sends
 * enum strings (KYC, document, interest, assignment, charge codes); the console never shows them raw.
 */
export type CodeGroup =
  | 'kyc'
  | 'userStatus'
  | 'documentStatus'
  | 'documentType'
  | 'interestStatus'
  | 'interestEvent'
  | 'closeReason'
  | 'assignmentStatus'
  | 'chargeType'
  | 'chargeStatus'
  | 'vehicleStatus'
  | 'role'
  | 'paymentStatus';

/** `RATE_MISMATCH` → "Rate mismatch": the safe fallback for a code the catalog does not know yet. */
export function humanizeCode(code: string): string {
  const words = code.trim().replace(/[_\s]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Plain label for an API code; an unknown code reads as words, null as "Unknown" — never the raw string. */
export function codeLabel(group: CodeGroup, code: string | null | undefined): string {
  if (code == null || code.trim() === '') return i18next.t('codes.unknown');
  // A runtime code is only a catalog key when `exists` says so, so look it up as a plain string.
  const key = `codes.${group}.${code}`;
  return i18next.exists(key) ? translateDynamicKey(key) : humanizeCode(code);
}

/**
 * `t` for a key built at runtime. Typing it against the catalog's full key union (`ParseKeys`) makes
 * TypeScript give up (TS2859) once the catalog is large, so the one narrow cast to a string key lives here.
 */
function translateDynamicKey(key: string): string {
  const t = i18next.t as unknown as (this: typeof i18next, key: string) => string;
  return String(t.call(i18next, key));
}
